/**
 * Pending Registrations Page
 *
 * Displays a list of pending organization registrations.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Mail, Phone, Clock, CheckCircle, XCircle } from 'lucide-react';
import type { ColumnDef, SortingState } from '@tanstack/react-table';

import {
  DataTable,
  Badge,
  Card,
  CardHeader,
  CardBody,
} from '@/components/common';
import { organizationService } from '@/api/services';
import type { PendingRegistration, ListRequest } from '@/api/types';
import { formatDateTime } from '@/utils/date.utils';
import { useToast } from '@/hooks/useToast';

// ============================================
// Page Component
// ============================================

export function PendingRegistrationsPage() {
  const toast = useToast();

  // State
  const [data, setData] = useState<PendingRegistration[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Fetch data
  const fetchPendingRegistrations = useCallback(
    async (params: { pageIndex: number; pageSize: number; sorting?: SortingState; search?: string }) => {
      setIsLoading(true);
      try {
        const request: ListRequest = {
          paging: {
            page: params.pageIndex + 1,
            size: params.pageSize,
          },
          sorting:
            params.sorting && params.sorting.length > 0
              ? { [params.sorting[0].id]: params.sorting[0].desc ? 'desc' : 'asc' }
              : undefined,
          filtering: params.search ? { email: params.search } : undefined,
        };

        const result = await organizationService.getPendingRegistrationList(request);
        setData(result.list);
        setTotalRows(result.filteredRecords);
      } catch (error) {
        console.error('Failed to fetch pending registrations:', error);
        toast.error('Failed to load pending registrations');
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Debounce search term
  useEffect(() => {
    // If search is cleared, update immediately
    if (searchTerm === '') {
      setDebouncedSearchTerm('');
      return;
    }

    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Initial load
  useEffect(() => {
    fetchPendingRegistrations({ ...pagination, sorting, search: debouncedSearchTerm });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, sorting, debouncedSearchTerm]);

  // Handle pagination change
  const handlePaginationChange = useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      setPagination(newPagination);
    },
    []
  );

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  // Handle search change
  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchTerm(value);
      setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
    },
    []
  );

  // Column definitions
  const columns = useMemo<ColumnDef<PendingRegistration>[]>(
    () => [
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Mail className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <span>{row.original.email || 'N/A'}</span>
            </div>
            {row.original.isEmailVerified ? (
              <CheckCircle className="h-4 w-4 text-success" />
            ) : (
              <XCircle className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
            )}
          </div>
        ),
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Phone className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
              <span>{row.original.mobile || 'N/A'}</span>
            </div>
            {row.original.isMobileVerified ? (
              <CheckCircle className="h-4 w-4 text-success" />
            ) : (
              <XCircle className="h-4 w-4 text-foreground-muted dark:text-foreground-dark-muted" />
            )}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={row.original.isRegistered ? 'success' : 'warning'}
            dot
          >
            {row.original.isRegistered ? 'Registered' : 'Pending'}
          </Badge>
        ),
      },
      {
        accessorKey: 'verification',
        header: 'Verification',
        cell: ({ row }) => {
          const emailVerified = row.original.isEmailVerified;
          const mobileVerified = row.original.isMobileVerified;

          if (emailVerified && mobileVerified) {
            return <Badge variant="success">Fully Verified</Badge>;
          } else if (emailVerified || mobileVerified) {
            return <Badge variant="warning">Partially Verified</Badge>;
          }
          return <Badge variant="default">Not Verified</Badge>;
        },
      },
      {
        accessorKey: 'createdDate',
        header: 'Created',
        cell: ({ row }) => (
          <div className="flex items-center gap-1 text-foreground-muted dark:text-foreground-dark-muted">
            <Clock className="h-3 w-3" />
            {row.original.createdDate ? formatDateTime(row.original.createdDate) : 'N/A'}
          </div>
        ),
      },
      {
        accessorKey: 'isRegistered',
        header: 'Registered',
        cell: ({ row }) => (
          <span className={row.original.isRegistered ? 'text-success' : 'text-foreground-muted dark:text-foreground-dark-muted'}>
            {row.original.isRegistered ? 'Yes' : 'No'}
          </span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
          Pending Registrations
        </h1>
        <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
          View and manage pending organization registrations
        </p>
      </div>

      {/* Data Table */}
      <Card>
        <CardHeader
          title="Pending Registration List"
          description={`${totalRows} pending registration${totalRows !== 1 ? 's' : ''}`}
        />
        <CardBody className="p-0">
          <DataTable
            columns={columns}
            data={data}
            isLoading={isLoading}
            searchable
            searchPlaceholder="Search by email..."
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            paginated
            totalRows={totalRows}
            pageIndex={pagination.pageIndex}
            pageSize={pagination.pageSize}
            onPaginationChange={handlePaginationChange}
            sortingState={sorting}
            onSortingChange={handleSortingChange}
            emptyMessage="No pending registrations"
            emptyDescription="There are no pending organization registrations."
            className="border-0"
          />
        </CardBody>
      </Card>
    </div>
  );
}

export default PendingRegistrationsPage;
